import { captureException, captureMessage } from "@sentry/react";
import * as Sentry from "@sentry/core";
import React, { createContext, useEffect, useReducer } from 'react';
import useKonamiCode from "v4/hooks/useKonamiCode";
import { randomId } from "v4/utils";
import { useDebugBarContext } from "v4/features/front/debugBar/contexts/DebugBarContext";
import { generateUrl, MIDDLEWARE_URL } from "v4/services/api.service";
import { DEBUG_BAR, USER } from "v4/data/apiRoutes";
import UserService from "api/user/user";
import { AppVersionContextExternal, OUTDATED_FRONT_TYPE, outdatedFrontState } from "v4/contexts/AppVersionContext";
import isNewerVersion from "v4/utils/isNewerVersion";
import useFetch from "v4/hooks/useFetch";
import { useTranslation } from "react-i18next";
import useNotification from "v4/hooks/useNotification";

export const MAINTENANCE_HEADER = 'X-POL-MAINTENANCE';
export const LATEST_FRONT_VERSION_HEADER = 'X-Pol-Front-Version-Latest';
export const TRANSACTION_ID_HEADER = 'X-Transaction-Id';

// savedFetch permet de sauvegarder le window.fetch original et de ne pas réappliquer un proxy à ce dernier (voir AuthProvider())
let savedFetch = null;

const defaultAuth = {
    token: UserService.getToken(),
    user: null,
    isMaintenance: null,
    setToken: () => {
    },
    logOut: () => {
    },
    switchUser: (token) => {
    },
    revertSwitchUser: () => {
    }
}

export const AuthContext = createContext(defaultAuth);

export const useAuthContext = () => {
    const context = React.useContext(AuthContext);

    if (context === undefined) {
        throw new Error('useAuthContext must be used within an AuthProvider');
    }

    return context;
}

/**
 *
 * @param state
 * @param type
 * @param payload
 * @return {{token: (null|*)}}
 */
function authReducer(state, { type, payload }) {
    switch (type) {
        case 'SET_TOKEN':
            UserService.setToken(payload.token);
            return { token: payload.token };
        case 'SET_USER':
            UserService.setUser(payload.user);
            return { ...state, user: payload.user };
        case 'SET_MAINTENANCE':
            return { ...state, isMaintenance: payload };
        case 'LOG_OUT':
            UserService.logout();
            return { ...defaultAuth, token: null, user: null, isMaintenance: null };
        case 'SWITCH_TOKEN':
            UserService.setSwitchToken(payload.token);
            return { ...state, token: payload.token };
        case 'REVERT_SWITCH_USER':
            UserService.removeSwitchToken();
            return { ...state, token: UserService.getOriginalToken() };
        default:
            const err = new Error('Aucun type ne correspond');
            captureException(err);
            throw err
    }
}

export function AuthProvider({ children }) {
    const { t } = useTranslation();
    const { addErrorNotification } = useNotification();
    const { addToken } = useDebugBarContext();
    const [auth, dispatch] = useReducer(authReducer, defaultAuth);
    const isKonamiActivated = useKonamiCode();
    const [{ data, isError }, userFetch] = useFetch();

    const setToken = (token) => {
        dispatch({ type: "SET_TOKEN", payload: { token } });

        if (UserService.isSSOOnly()) {
            dispatch({ type: 'SET_USER', payload: { user: { id: 'sso_user', customer: { id: 'sso_customer' } } } });
            return;
        }

        userFetch({ url: generateUrl(USER) });
    };

    const logOut = () => {
        dispatch({ type: "LOG_OUT" });
    }

    const switchUser = (token) => {
        dispatch({ type: "SWITCH_TOKEN", payload: { token } });
    }

    const revertSwitchUser = () => {
        dispatch({ type: "REVERT_SWITCH_USER" });
    }

    const authProvided = {
        ...auth,
        setToken,
        logOut,
        switchUser,
        revertSwitchUser,
    }

    useEffect(() => {
        if (!UserService.isTokenExpired()) return

        if (UserService.getTokenData() && UserService.isTokenExpired()) {
            addErrorNotification(t('expired_token'));
        }

        logOut();
    }, [addErrorNotification, auth.token, t]);

    useEffect(() => {
        // Si, lors de la récupération de l'utilisateur, une erreur est survenue,
        // on déconnecte l'utilisateur et donc retour à la page de connexion
        if (isError) return logOut();

        if (!auth.token || !data) return;

        dispatch({ type: 'SET_USER', payload: { user: data } });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, isError]);

    // À chaque fois que AuthProvider est mis à jour, il remplace window.fetch par la fonction window.fetch originale
    if (savedFetch) {
        window.fetch = savedFetch;
    } else {
        // Permet de setter savedFetch avec la fonction window.fetch originale une seule fois
        savedFetch = window.fetch;
    }

    // À chaque fois que AuthProvider est mis à jour, il remplace window.fetch qui,
    // maintenant, est la fonction originale par notre proxy. Nécessaire de re-setter
    // à chaque fois pour être sûr d'avoir le bon token
    window.fetch = new Proxy(window.fetch, {
        async apply(fetch, that, args) {
            let [url = '', config = {}] = args;

            if (!config.headers) config.headers = {};

            const activeSpan = Sentry.getActiveSpan();
            const rootSpan = activeSpan ? Sentry.getRootSpan(activeSpan) : undefined;

            // Create `sentry-trace` header
            const sentryTraceHeader = rootSpan
                ? Sentry.spanToTraceHeader(rootSpan)
                : undefined;

            // Create `baggage` header
            const sentryBaggageHeader = rootSpan
                ? Sentry.spanToBaggageHeader(rootSpan)
                : undefined;

            // Set-up Sentry Tracing
            if (sentryTraceHeader) config.headers['sentry-trace'] = sentryTraceHeader;
            if (sentryBaggageHeader) config.headers['baggage'] = sentryBaggageHeader;

            // Set-up X-Transaction-ID
            if (url.match(process.env.REACT_APP_HOST_API) || url.match(process.env.REACT_APP_POL_CORE_URL)) config.headers[TRANSACTION_ID_HEADER] = `front:${randomId()}`;
            Sentry.setTag('X-Transaction-Id', config.headers[TRANSACTION_ID_HEADER] ?? 'no-transaction-id');

            // Version du front envoyée à pol-middleware et pol-core pour le contrôle de version minimale
            if (process.env.REACT_APP_VERSION && (url.match(process.env.REACT_APP_HOST_API) || url.match(process.env.REACT_APP_POL_CORE_URL))) {
                config.headers['X-Pol-Front-Version'] = process.env.REACT_APP_VERSION;
            }

            // Si le lien correspond à l'API (excepté lors de la connexion et de la configuration du client Microsoft Graph)
            if (url.match(process.env.REACT_APP_HOST_API) && !url.match(/login/g) && !url.endsWith('/module_microsoft_graph/customer_configuration')) {
                // On ajoute le delcache=true pour debug si le konami code est activé
                if (isKonamiActivated) {
                    const _url = new URL(url);
                    _url.searchParams.set('delcache', 'true');
                    url = _url.toString();
                }

                if (!config.notoken && UserService.isTokenExpired()) {
                    // Throw an error with TokenExpired name to be able to catch it in useFetch
                    throw Object.assign(new Error('Token expired'), { name: 'TokenExpired' });
                }

                let tokenToUse = authProvided.token;

                if (url.includes('/switch-user/') && UserService.isUserSwitching()) {
                    tokenToUse = UserService.getOriginalToken();
                }

                if (tokenToUse && !config.notoken) {
                    config.headers["X-POL-AUTH"] = 'Bearer ' + tokenToUse;
                }
            }

            // Si le lien correspond à l'API de core (excepté lors de la connexion)
            if (url.match(process.env.REACT_APP_POL_CORE_URL) && !url.match(/login/g)) {
                let tokenToUse = authProvided.token;

                if (url.includes('/switch-user/') && UserService.isUserSwitching()) {
                    tokenToUse = UserService.getOriginalToken();
                }

                if (tokenToUse && !config.notoken) {
                    config.headers["Authorization"] = 'Bearer ' + tokenToUse;
                }
            }

            const result = await fetch.apply(that, [url, config]);

            /* Gestion de la réponse avant de l'envoyer au reste du code */

            // Gestion de la debugbar si activée
            if (process.env.REACT_APP_DEBUGBAR_ENABLED === 'true') {
                const debugToken = result.headers.get('X-Debug-Token');
                if (debugToken && !url.match(`${MIDDLEWARE_URL}${generateUrl(DEBUG_BAR, { token: debugToken }, false)}`)) {
                    setTimeout(() => addToken(debugToken, Date.now()), 100); // L'appel est parfois trop rapide pour avoir les infos et l'on a une 404, ce mini délai permet de ne pas avoir ce problème
                }
            }

            // Gestion des erreurs
            if (!result.ok) {

                // Le token a expiré ou n'est pas valide, on déconnecte l'utilisateur sauf
                // s'il n'a pas de token (par exemple lors de la connexion)
                if (result.status === 401 && authProvided.token) {
                    addErrorNotification(t('expired_token'));
                    logOut();
                }

                let isOutdatedFront = false;

                if (result.status === 410) {
                    const json = await result.clone().json().catch(() => null);
                    isOutdatedFront = json?.type === OUTDATED_FRONT_TYPE;
                }

                if (isOutdatedFront) {
                    outdatedFrontState.isOutdated = true;

                    if (AppVersionContextExternal.notifyOutdated) {
                        AppVersionContextExternal.notifyOutdated();
                    }
                }

                if (result.status !== 401 && !isOutdatedFront) {
                    // On capture l'erreur dans Sentry
                    captureMessage(`Error received when requesting API`, {
                        extra: {
                            request: {
                                url,
                                config: JSON.stringify(config),
                            },
                            response: {
                                status: result.status,
                                statusText: result.statusText,
                                body: result.body,
                            },
                        }
                    });
                }
            }

            if (result.headers.has(MAINTENANCE_HEADER)) {
                dispatch({ type: 'SET_MAINTENANCE', payload: result.headers.get(MAINTENANCE_HEADER) === 'true' });
            }

            if (UserService.isLogged()
                && isNewerVersion(result.headers.get(LATEST_FRONT_VERSION_HEADER), process.env.REACT_APP_VERSION)
                && AppVersionContextExternal.notifyUpdateAvailable) {
                AppVersionContextExternal.notifyUpdateAvailable();
            }

            return result;
        }
    });

    return (
        <AuthContext.Provider value={authProvided}>
            {children}
        </AuthContext.Provider>
    )
}
